import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SparklesIcon,
  PaperAirplaneIcon,
  PlusIcon,
  ClockIcon,
  TrashIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import {
  clientsAPI, projectsAPI, adhocQueryAPI,
  Client as ClientType, Project as ProjectType, AdhocMessage, ChatSessionSummary,
} from '../services/api';

const getLocalDateString = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const EXAMPLE_PROMPTS = [
  'What percentage of my time was spent in meetings vs. development?',
  'How many hours did I log each month, and what was the biggest category?',
  'Summarize what the attached notes say about open issues.',
  'What were my largest expenses and which project did they belong to?',
];

export const AdhocQueryPage: React.FC = () => {
  const { t } = useTranslation();

  const today = new Date();
  const [startDate, setStartDate] = useState(getLocalDateString(new Date(today.getFullYear(), 0, 1)));
  const [endDate, setEndDate] = useState(getLocalDateString(new Date(today.getFullYear(), 11, 31)));

  const [clients, setClients] = useState<ClientType[]>([]);
  const [projects, setProjects] = useState<ProjectType[]>([]);
  const [filterClientId, setFilterClientId] = useState('');
  const [filterProjectId, setFilterProjectId] = useState('');

  const [messages, setMessages] = useState<AdhocMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');

  const [sessions, setSessions] = useState<ChatSessionSummary[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);

  const loadSessions = () => adhocQueryAPI.listSessions().then(setSessions).catch(() => {});

  useEffect(() => {
    clientsAPI.list().then(setClients).catch(() => {});
    projectsAPI.list().then(setProjects).catch(() => {});
    loadSessions();
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, isLoading]);

  const filteredProjects = filterClientId
    ? projects.filter(p => p.client_id === filterClientId)
    : projects;

  const send = async (text: string) => {
    const question = text.trim();
    if (!question || isLoading) return;
    if (startDate > endDate) {
      setError('Start date must be before or equal to end date');
      return;
    }
    setError('');
    setWarning('');
    const nextMessages: AdhocMessage[] = [...messages, { role: 'user', content: question }];
    setMessages(nextMessages);
    setInput('');
    setIsLoading(true);
    try {
      const res = await adhocQueryAPI.ask({
        start_date: startDate,
        end_date: endDate,
        client_id: filterClientId || undefined,
        project_id: filterProjectId || undefined,
        messages: nextMessages,
        session_id: currentSessionId || undefined,
      });
      setMessages([...nextMessages, { role: 'assistant', content: res.answer }]);
      if (res.warning) setWarning(res.warning);
      setCurrentSessionId(res.session_id);  // autosaved server-side
      loadSessions();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'The assistant request failed. Please try again.');
      // roll back the optimistic user message so they can retry
      setMessages(messages);
      setInput(question);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send(input);
    }
  };

  const resetChat = () => {
    setMessages([]);
    setError('');
    setWarning('');
    setInput('');
    setCurrentSessionId(null);
    setShowHistory(false);
  };

  const loadSession = async (id: string) => {
    setShowHistory(false);
    try {
      const s = await adhocQueryAPI.getSession(id);
      setMessages(s.messages);
      setCurrentSessionId(s.id);
      setFilterClientId(s.client_id || '');
      setFilterProjectId(s.project_id || '');
      if (s.start_date) setStartDate(s.start_date);
      if (s.end_date) setEndDate(s.end_date);
      setError('');
      setWarning('');
    } catch {
      setError('Failed to load that chat.');
    }
  };

  const handleDeleteSession = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await adhocQueryAPI.deleteSession(id);
      setSessions(prev => prev.filter(s => s.id !== id));
      if (currentSessionId === id) resetChat();
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
            <SparklesIcon className="h-7 w-7 mr-2 text-green-600 dark:text-green-400" />
            {t('adhoc.title')}
          </h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            {t('adhoc.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="relative">
            <button
              onClick={() => setShowHistory(o => !o)}
              className="inline-flex items-center px-3 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg transition-colors"
              title="Chat history"
            >
              <ClockIcon className="h-4 w-4 mr-1.5" />
              History{sessions.length > 0 ? ` (${sessions.length})` : ''}
            </button>
            {showHistory && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowHistory(false)} />
                <div className="absolute right-0 mt-1 w-72 max-h-[70vh] overflow-y-auto bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-20">
                  {sessions.length === 0 ? (
                    <div className="px-3 py-6 text-sm text-gray-500 dark:text-gray-400 text-center">No saved chats yet.</div>
                  ) : (
                    sessions.map(s => (
                      <div
                        key={s.id}
                        onClick={() => loadSession(s.id)}
                        className={`flex items-center justify-between gap-2 px-3 py-2 cursor-pointer transition-colors ${
                          currentSessionId === s.id
                            ? 'bg-green-50 dark:bg-green-900/20'
                            : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        <div className="min-w-0">
                          <div className="text-sm text-gray-900 dark:text-white truncate">{s.title}</div>
                          <div className="text-xs text-gray-400 dark:text-gray-500">
                            {new Date(s.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} · {s.message_count} message{s.message_count !== 1 ? 's' : ''}
                          </div>
                        </div>
                        <button
                          onClick={(e) => handleDeleteSession(s.id, e)}
                          className="p-1 text-gray-400 hover:text-red-500 rounded flex-shrink-0"
                          title="Delete chat"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </div>
          <button
            onClick={resetChat}
            className="inline-flex items-center px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <PlusIcon className="h-4 w-4 mr-1.5" />
            {t('adhoc.newChat')}
          </button>
        </div>
      </div>

      {/* Scope selectors */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('adhoc.client')}
            </label>
            <select
              value={filterClientId}
              onChange={(e) => { setFilterClientId(e.target.value); setFilterProjectId(''); }}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="">{t('adhoc.allClients')}</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('adhoc.project')}
            </label>
            <select
              value={filterProjectId}
              onChange={(e) => setFilterProjectId(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="">{t('adhoc.allProjects')}</option>
              {filteredProjects.map(p => (
                <option key={p.id} value={p.id}>{p.name}{p.client_name ? ` (${p.client_name})` : ''}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('adhoc.startDate')}
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('adhoc.endDate')}
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>
        </div>
        {startDate > endDate && (
          <p className="mt-2 text-sm text-red-600 dark:text-red-400">
            Start date must be before or equal to end date
          </p>
        )}
      </div>

      {/* Chat panel */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 flex flex-col" style={{ height: '55vh', minHeight: 360 }}>
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center px-4">
              <SparklesIcon className="h-12 w-12 text-gray-300 dark:text-gray-600" />
              <h3 className="mt-3 text-sm font-medium text-gray-900 dark:text-white">
                {t('adhoc.emptyTitle')}
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 max-w-md">
                {t('adhoc.emptyDescription')}
              </p>
              <div className="mt-4 flex flex-wrap justify-center gap-2 max-w-xl">
                {EXAMPLE_PROMPTS.map((p) => (
                  <button
                    key={p}
                    onClick={() => send(p)}
                    className="px-3 py-1.5 text-xs text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-full transition-colors"
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap break-words ${
                    m.role === 'user'
                      ? 'bg-green-600 text-white rounded-br-sm'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-bl-sm'
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))
          )}

          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-gray-100 dark:bg-gray-700 rounded-2xl rounded-bl-sm px-4 py-3">
                <div className="flex space-x-1.5">
                  <span className="h-2 w-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="h-2 w-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="h-2 w-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Warning / error */}
        {warning && (
          <div className="mx-4 mb-2 flex items-start gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-2.5">
            <ExclamationTriangleIcon className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700 dark:text-amber-300">{warning}</p>
          </div>
        )}
        {error && (
          <div className="mx-4 mb-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-2.5">
            <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {/* Input */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-3">
          <div className="flex items-end gap-2">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
              placeholder={t('adhoc.placeholder')}
              className="flex-1 resize-none max-h-32 px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
            <button
              onClick={() => send(input)}
              disabled={isLoading || !input.trim()}
              className="inline-flex items-center justify-center h-10 w-10 flex-shrink-0 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 dark:disabled:bg-gray-600 text-white rounded-lg transition-colors"
              aria-label={t('adhoc.send')}
            >
              <PaperAirplaneIcon className="h-5 w-5" />
            </button>
          </div>
          <p className="mt-1.5 text-xs text-gray-400 dark:text-gray-500">
            {t('adhoc.disclaimer')}
          </p>
        </div>
      </div>
    </div>
  );
};
